import Grid from "@bmi-digital/components/grid";
import Hero from "@bmi-digital/components/hero";
import Search from "@bmi-digital/components/search";
import Section from "@bmi-digital/components/section";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { graphql } from "gatsby";
import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import BackToResults from "../../components/BackToResults";
import Breadcrumbs from "../../components/Breadcrumbs";
import Filters from "../../components/FiltersSidebar";
import { generateGetMicroCopy } from "../../components/MicroCopy";
import Page from "../../components/Page";
import ProgressIndicator from "../../components/ProgressIndicator";
import Scrim from "../../components/Scrim";
import { updateBreadcrumbTitleFromPayload } from "../../utils/breadcrumbUtils";
import createImageProps from "../../components/image/createImageProps";
import TrainingCatalogue from "./components/training-catalogue";
import TrainingNoResults from "./components/training-no-results";
import { useTrainings } from "./hooks/useTrainings";
import {
  CataloguesContainer,
  SearchWrapper,
  StyledSearchTitle
} from "./styles";
import { TrainingListerPageProps } from "./types";

const TrainingListerPage = ({ data }: TrainingListerPageProps) => {
  const {
    breadcrumbs,
    breadcrumbTitle,
    featuredMedia,
    filters: defaultFilters,
    path,
    meta,
    subtitle,
    title,
    searchTips
  } = data.payloadTrainingListerPage;
  const theme = useTheme();

  const enhancedBreadcrumbs = updateBreadcrumbTitleFromPayload(
    breadcrumbs || [],
    breadcrumbTitle
  );

  const {
    filters,
    handleFiltersChange,
    handleResetFilters,
    groupedTrainings,
    fetchPaginatedTrainings,
    collapseCatalogueCourses,
    initialLoading,
    total,
    searchQuery
  } = useTrainings({ defaultFilters });

  const getMicroCopy = generateGetMicroCopy(data.allPayloadMicroCopy.nodes);

  const searchLabel = getMicroCopy(microCopy.TRAINING_LISTING_SEARCH_LABEL);
  const searchPlaceholder = getMicroCopy(
    microCopy.TRAINING_LISTING_SEARCH_PLACEHOLDER
  );

  const disabledClearAllButton = useMemo(() => {
    return filters.every((filter) => !filter.value?.length);
  }, [filters]);

  return (
    <Page
      title={title}
      pageData={{
        meta,
        path,
        signupBlock: null,
        breadcrumbs: enhancedBreadcrumbs
      }}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      ogImageUrl={featuredMedia?.sizes.heroDesktop.url}
    >
      {initialLoading && (
        <Scrim theme="light">
          <ProgressIndicator theme="light" />
        </Scrim>
      )}
      <Hero
        level={1}
        title={title}
        media={
          featuredMedia
            ? createImageProps(
                {
                  ...featuredMedia,
                  size: "cover",
                  loading: "eager"
                },
                theme
              )
            : undefined
        }
        breadcrumbs={
          <BackToResults isDarkThemed data-testid="breadcrumbs-hero-section">
            <Breadcrumbs
              data={enhancedBreadcrumbs}
              isDarkThemed
              data-testid="training-landing-page-hero-breadcrumbs"
            />
          </BackToResults>
        }
      >
        {subtitle}
      </Hero>
      <Section>
        <Grid container spacing={3}>
          <Grid xs={12} md={12} lg={3}>
            <SearchWrapper>
              <StyledSearchTitle variant="h5">{searchLabel}</StyledSearchTitle>
              <Search
                label={searchLabel}
                value={searchQuery}
                placeholder={searchPlaceholder}
                data-testid="training-list-search-form"
              />
            </SearchWrapper>
            <Filters
              filters={filters}
              disableClearAllBtn={disabledClearAllButton}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleResetFilters}
              filtersTitle={getMicroCopy(microCopy.TRAINING_FILTERS_TITLE)}
              clearAllBtnLabel={getMicroCopy(
                microCopy.TRAINING_FILTERS_CLEAR_ALL_LABEL
              )}
            />
          </Grid>
          <CataloguesContainer xs={12} md={12} lg={9}>
            {Object.keys(groupedTrainings).length > 0 &&
              Object.entries(groupedTrainings).map(([catalogueId, courses]) => (
                <TrainingCatalogue
                  key={`training-catalogue-${catalogueId}`}
                  defaultImageUrl={featuredMedia?.sizes.heroDesktop.url}
                  courses={courses}
                  countryCode={data.payloadSite.countryCode}
                  fetchPaginatedTrainings={fetchPaginatedTrainings}
                  collapseCatalogueCourses={collapseCatalogueCourses}
                  // eslint-disable-next-line security/detect-object-injection
                  total={total[catalogueId]}
                />
              ))}
            {Object.keys(groupedTrainings).length === 0 && !initialLoading && (
              <TrainingNoResults searchTips={searchTips?.[0]} />
            )}
          </CataloguesContainer>
        </Grid>
      </Section>
      <Section data-testid="training-lister-page-breadcrumbs-section-bottom">
        <BackToResults>
          <Breadcrumbs
            data={enhancedBreadcrumbs}
            data-testid="training-lister-page-breadcrumbs-bottom"
          />
        </BackToResults>
      </Section>
    </Page>
  );
};

export default TrainingListerPage;

export const pageQuery = graphql`
  query TrainingListerPageById($pageId: String!) {
    payloadTrainingListerPage(id: { eq: $pageId }) {
      ...PayloadTrainingListerPageFragment
      breadcrumbTitle
      slug
      title
      subtitle
      featuredMedia {
        ...ImageHeroFragment
      }
      filters {
        filterCode
        label
        name
        options {
          label
          value
        }
      }
      searchTips {
        ...TitleWithContentFragment
      }
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
